import { Component, ElementRef, HostListener, inject, signal, WritableSignal } from '@angular/core'
import { NgxTippyModule } from 'ngx-tippy-wrapper'
export type DateRange = { startDate: string | null; endDate: string | null }

/** Inclusive `YYYY-MM-DD` bounds; days in range are treated as disabled when selecting. */
export type DisabledYearMonthDayRange = { start: string; end: string }

@Component({
  selector: 'app-date-range-picker-content',
  standalone: true,
  imports: [NgxTippyModule],
  templateUrl: './date-range-picker-content.component.html',
  styleUrl: './date-range-picker-content.component.scss'
})
export class DateRangePickerContentComponent {
  private readonly _hostElement = inject(ElementRef<HTMLElement>)

  public readonly initialStartDate: WritableSignal<string | null> = signal<string | null>(null)
  public readonly initialEndDate: WritableSignal<string | null> = signal<string | null>(null)
  public readonly initialIsIndefinitely: WritableSignal<boolean | undefined> = signal<boolean | undefined>(undefined)
  public onApply: (range: DateRange) => void = () => {}
  public closeDropdown: () => void = () => {}

  /**
   * Close the dropdown when the Escape key is pressed
   * @param event - The keyboard event
   */
  @HostListener('document:keydown', ['$event'])
  protected onDocumentKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') return
    if (!this._hostElement.nativeElement.closest('app-dropdown-content')) return
    this.closeDropdown()
    event.preventDefault()
    event.stopImmediatePropagation()
  }

  public readonly showIndefiniteOption: WritableSignal<boolean> = signal(true)
  public readonly minSelectableDate: WritableSignal<string | null> = signal<string | null>(null)
  public readonly maxSelectableDate: WritableSignal<string | null> = signal<string | null>(null)
  public readonly initialVisibleMonthDate: WritableSignal<string | null> = signal<string | null>(null)
  public readonly disabledDateRanges: WritableSignal<DisabledYearMonthDayRange[]> = signal<DisabledYearMonthDayRange[]>([])
  public readonly silentDisabledDateRanges: WritableSignal<DisabledYearMonthDayRange[]> = signal<DisabledYearMonthDayRange[]>([])
  public readonly allowedWeekDays: WritableSignal<string[] | null> = signal<string[] | null>(null)
  public readonly compactCalendarOnMobile: WritableSignal<boolean> = signal(false)
  public readonly closedExceptionDayTippyProps = {
    theme: 'dark' as const,
    appendTo: () => document.body,
    zIndex: 200_000
  }
  public readonly leftMonth = signal<Date>(new Date())
  public readonly rightMonth = signal<Date>(new Date())
  public readonly rangeStart = signal<Date | null>(null)
  public readonly rangeEnd = signal<Date | null>(null)
  public readonly runsIndefinitely = signal(false)
  private _init = false

  /** Called by parent via dataSetter after setting initial values */
  public init(): void {
    if (this._init) return
    this._init = true
    this._syncUiFromInitials()
  }

  /**
   * Restore selection and visible months from {@link initialStartDate} / {@link initialEndDate} /
   * {@link initialIsIndefinitely} without applying or closing the picker.
   */
  public reset(): void {
    this._syncUiFromInitials()
  }

  /** Sync UI from initial values */
  private _syncUiFromInitials(): void {
    const startDate = this.initialStartDate()
    const endDate = this.initialEndDate()
    const showIndefiniteOption = this.showIndefiniteOption()
    const start = startDate ? this._parseDate(startDate) : null
    const end = endDate ? this._parseDate(endDate) : null
    if (!showIndefiniteOption) {
      this.runsIndefinitely.set(false)
      if (start) this.rangeStart.set(start)
      if (end) this.rangeEnd.set(end)
      else if (start) this.rangeEnd.set(start)
    } else {
      if (start) this.rangeStart.set(start)
      const explicitIndefinite = this.initialIsIndefinitely()
      if (explicitIndefinite === true) {
        this.rangeEnd.set(null)
      } else if (end) {
        this.rangeEnd.set(end)
      }
      const runsIndef = explicitIndefinite === true || explicitIndefinite === false ? explicitIndefinite : Boolean(start) && !end
      this.runsIndefinitely.set(runsIndef)
    }
    const initialVisibleMonthDate = this.initialVisibleMonthDate()
    const anchor = initialVisibleMonthDate ? this._parseDate(initialVisibleMonthDate) : null
    const base = anchor ?? start ?? end ?? new Date()
    const left = new Date(base.getFullYear(), base.getMonth(), 1)
    const right = new Date(base.getFullYear(), base.getMonth() + 1, 1)
    this.leftMonth.set(left)
    this.rightMonth.set(right)
  }

  /** Ensure right month is always left+1 (call after left month changes externally) */
  public ensureRightMonth(): void {
    const left = this.leftMonth()
    const right = new Date(left.getFullYear(), left.getMonth() + 1, 1)
    this.rightMonth.set(right)
  }

  private _parseDate(s: string): Date | null {
    const d = new Date(s + 'T12:00:00')
    return isNaN(d.getTime()) ? null : d
  }

  public get leftMonthDate(): Date {
    return this.leftMonth()
  }

  public get rightMonthDate(): Date {
    return this.rightMonth()
  }

  /**
   * Go to the previous month
   */
  public prevMonth(): void {
    const left = new Date(this.leftMonth())
    left.setMonth(left.getMonth() - 1)
    this.leftMonth.set(left)
    const right = new Date(this.rightMonth())
    right.setMonth(right.getMonth() - 1)
    this.rightMonth.set(right)
  }

  /**
   * Go to the next month
   */
  public nextMonth(): void {
    const left = new Date(this.leftMonth())
    left.setMonth(left.getMonth() + 1)
    this.leftMonth.set(left)
    const right = new Date(this.rightMonth())
    right.setMonth(right.getMonth() + 1)
    this.rightMonth.set(right)
  }

  /**
   * Get the days for a month
   * @param date - The date to get the days for
   * @returns The days for the month
   */
  public getDaysForMonth(date: Date): { date: Date; isCurrentMonth: boolean }[] {
    const year = date.getFullYear()
    const month = date.getMonth()
    const first = new Date(year, month, 1)
    const last = new Date(year, month + 1, 0)
    const startDay = first.getDay()
    const days: { date: Date; isCurrentMonth: boolean }[] = []
    const prevMonth = new Date(year, month, 0)
    const prevCount = prevMonth.getDate()
    for (let i = 0; i < startDay; i++) {
      days.push({
        date: new Date(year, month - 1, prevCount - startDay + i + 1),
        isCurrentMonth: false
      })
    }
    for (let d = 1; d <= last.getDate(); d++) {
      days.push({ date: new Date(year, month, d), isCurrentMonth: true })
    }
    const remaining = 42 - days.length
    for (let d = 1; d <= remaining; d++) {
      days.push({ date: new Date(year, month + 1, d), isCurrentMonth: false })
    }
    return days
  }

  /**
   * Convert a date to a year-month-day string
   * @param date - The date to convert
   * @returns The year-month-day string
   */
  private _toYearMonthDay(date: Date): string {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  }

  /**
   * True if the date is in the range
   * @param date - The date to check
   * @returns True if the date is in the range
   */
  public isInRange(d: Date): boolean {
    const start = this.rangeStart()
    const end = this.rangeEnd()
    if (!start) return false
    const t = d.getTime()
    const s = start.getTime()
    if (!end) return t === s
    const e = end.getTime()
    return t >= Math.min(s, e) && t <= Math.max(s, e)
  }

  public isRangeStart(date: Date): boolean {
    const start = this.rangeStart()
    if (!start) return false
    return this._toYearMonthDay(date) === this._toYearMonthDay(start)
  }

  public isRangeEnd(date: Date): boolean {
    const end = this.rangeEnd()
    if (!end) return false
    return this._toYearMonthDay(date) === this._toYearMonthDay(end)
  }

  public isSelected(date: Date): boolean {
    return this.isRangeStart(date) || this.isRangeEnd(date)
  }

  /** Start and end are set on two different calendar days (pill ends get sharp inner corners). */
  public isMultiDayRange(): boolean {
    const start = this.rangeStart()
    const end = this.rangeEnd()
    if (!start || !end) return false
    return this._toYearMonthDay(start) !== this._toYearMonthDay(end)
  }

  /**
   * Rounding for the day button: full pill when only one anchor day or same-day range;
   * flat inner edges between multi-day range endpoints.
   */
  public dayButtonRoundingClass(d: Date, isCurrentMonth: boolean): string {
    if (!isCurrentMonth || this.isDateDisabled(d)) return 'tw-rounded-lg'
    const multi = this.isMultiDayRange()
    const sel = this.isSelected(d)
    if (sel && (!this.rangeEnd() || !multi || (this.isRangeStart(d) && this.isRangeEnd(d)))) {
      return 'tw-rounded-lg'
    }
    if (sel && multi && this.isRangeStart(d) && !this.isRangeEnd(d)) {
      return 'tw-rounded-l-lg tw-rounded-r-none'
    }
    if (sel && multi && this.isRangeEnd(d) && !this.isRangeStart(d)) {
      return 'tw-rounded-r-lg tw-rounded-l-none'
    }
    if (this.isInRange(d) && !sel && multi) {
      return 'tw-rounded-none'
    }
    return 'tw-rounded-lg'
  }

  /** True if the date is before today (start of day in local time) */
  public isPastDate(d: Date): boolean {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const t = new Date(d.getFullYear(), d.getMonth(), d.getDate())
    return t.getTime() < today.getTime()
  }

  private _normalizedYmdRange(r: DisabledYearMonthDayRange): { start: string; end: string } {
    return r.start <= r.end ? { start: r.start, end: r.end } : { start: r.end, end: r.start }
  }

  /** True if `ymd` falls inside any {@link disabledDateRanges} (inclusive). */
  public isYmdInDisabledRanges(ymd: string): boolean {
    for (const raw of this.disabledDateRanges()) {
      const { start, end } = this._normalizedYmdRange(raw)
      if (ymd >= start && ymd <= end) return true
    }
    return false
  }

  /** True if `ymd` falls inside any {@link silentDisabledDateRanges} (inclusive).
   * @param ymd - The year-month-day to check
   * @returns True if the year-month-day is in the silent disabled ranges
   */
  public isYmdInSilentDisabledRanges(ymd: string): boolean {
    for (const raw of this.silentDisabledDateRanges()) {
      const { start, end } = this._normalizedYmdRange(raw)
      if (ymd >= start && ymd <= end) return true
    }
    return false
  }

  /** True when this calendar day falls in {@link disabledDateRanges} (expected: other closed exceptions only).
   * @param date - The date to check
   * @returns True if the date is blocked by closed exception range
   */
  public isDayBlockedByClosedExceptionRange(date: Date): boolean {
    return this.isYmdInDisabledRanges(this._toYearMonthDay(date))
  }

  /** Disabled if outside `minSelectableDate` / `maxSelectableDate` when set; otherwise past or future is disabled by default.
   * @param date - The date to check
   * @returns True if the date is disabled
   */
  public isDateDisabled(date: Date): boolean {
    if (this.isDayBlockedByAllowedWeekDays(date)) return true
    if (this.isYmdInDisabledRanges(this._toYearMonthDay(date))) return true
    if (this.isYmdInSilentDisabledRanges(this._toYearMonthDay(date))) return true
    const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
    const minSelectableDate = this.minSelectableDate()
    const maxSelectableDate = this.maxSelectableDate()

    if (minSelectableDate) {
      const minD = this._parseDate(minSelectableDate)
      if (minD) {
        const minStart = new Date(minD.getFullYear(), minD.getMonth(), minD.getDate()).getTime()
        if (dayStart < minStart) return true
      }
    }

    if (maxSelectableDate) {
      const maxD = this._parseDate(maxSelectableDate)
      if (maxD) {
        const maxStart = new Date(maxD.getFullYear(), maxD.getMonth(), maxD.getDate()).getTime()
        if (dayStart > maxStart) return true
      }
    }

    if (minSelectableDate || maxSelectableDate) return false
    return this.isPastDate(date)
  }

  private _weekdayShort(date: Date): string {
    return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()] ?? 'Sun'
  }

  /**
   * True if the date is blocked by allowed week days
   * @param date - The date to check
   * @returns True if the date is blocked by allowed week days
   */
  public isDayBlockedByAllowedWeekDays(date: Date): boolean {
    const allowed = this.allowedWeekDays()
    if (!allowed || allowed.length === 0) return false
    return !allowed.includes(this._weekdayShort(date))
  }

  /** True if `d` is strictly before `minSelectableDate` (when set and parseable).
   * @param date - The date to check
   * @returns True if the date is strictly before `minSelectableDate` (when set and parseable)
   */
  public isBeforeMinSelectableDate(date: Date): boolean {
    const minSelectableDate = this.minSelectableDate()
    if (!minSelectableDate) return false
    const minD = this._parseDate(minSelectableDate)
    if (!minD) return false
    const minStart = new Date(minD.getFullYear(), minD.getMonth(), minD.getDate()).getTime()
    const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
    return dayStart < minStart
  }

  /**
   * True if the date is closed (highlighted)
   * @param date - The date to check
   * @returns True if the date is closed (highlighted)
   */
  public isDayClosedHighlight(date: Date): boolean {
    if (this.isYmdInSilentDisabledRanges(this._toYearMonthDay(date))) return false
    if (this.isDayBlockedByClosedExceptionRange(date)) return true
    return this.isDayBlockedByAllowedWeekDays(date) && !this.isBeforeMinSelectableDate(date)
  }

  /**
   * True if any calendar day in the inclusive range [from, to] is disabled (blocked ranges, min date, past).
   * Used so a range cannot span across other exceptions’ dates.
   * @param from - The start date of the range
   * @param to - The end date of the range
   * @returns True if any calendar day in the inclusive range [from, to] is disabled
   */
  private _inclusiveRangeContainsDisabledDay(from: Date, to: Date): boolean {
    const low = from.getTime() <= to.getTime() ? from : to
    const high = from.getTime() <= to.getTime() ? to : from
    const cur = new Date(low.getFullYear(), low.getMonth(), low.getDate())
    const endDay = new Date(high.getFullYear(), high.getMonth(), high.getDate())
    while (cur.getTime() <= endDay.getTime()) {
      if (this.isDateDisabled(cur)) return true
      cur.setDate(cur.getDate() + 1)
    }
    return false
  }

  /**
   * Select a day
   * @param date - The date to select
   */
  public selectDay(date: Date): void {
    if (this.isDateDisabled(date)) return
    if (this.showIndefiniteOption() && this.runsIndefinitely()) {
      this.rangeStart.set(new Date(date.getFullYear(), date.getMonth(), date.getDate()))
      this.rangeEnd.set(null)
      return
    }
    const start = this.rangeStart()
    const end = this.rangeEnd()
    if (!start || (start && end)) {
      this.rangeStart.set(new Date(date.getFullYear(), date.getMonth(), date.getDate()))
      this.rangeEnd.set(null)
    } else {
      const date2 = new Date(date.getFullYear(), date.getMonth(), date.getDate())
      let newStart = start
      let newEnd = date2
      if (date2.getTime() < start.getTime()) {
        newEnd = start
        newStart = date2
      }
      if (this._inclusiveRangeContainsDisabledDay(newStart, newEnd)) {
        this.rangeStart.set(date2)
        this.rangeEnd.set(null)
        return
      }
      this.rangeStart.set(newStart)
      this.rangeEnd.set(newEnd)
    }
  }

  /** Apply the selected range */
  public apply(): void {
    const start = this.rangeStart()
    if (!start) {
      this.closeDropdown()
      return
    }
    const end = this.rangeEnd()
    const endOrStart = this.runsIndefinitely() ? null : end ?? start
    if (endOrStart && this._inclusiveRangeContainsDisabledDay(start, endOrStart)) {
      return
    }
    this.onApply({
      startDate: this._toYearMonthDay(start),
      endDate: this.runsIndefinitely() ? null : end ? this._toYearMonthDay(end) : this._toYearMonthDay(start)
    })
    this.closeDropdown()
  }

  public monthLabel(date: Date): string {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  }

  public weekDays(): string[] {
    return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  }
}
